import { useEffect, useMemo, useRef, useState } from 'react';
import { ExternalLink, FileText, Paperclip, Plus, Printer, Search, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Textarea } from '@/components/ui/Textarea';
import { Badge } from '@/components/ui/Badge';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { NewSupplierDialog } from '@/components/catalog/NewSupplierDialog';
import {
  PoImportBanner,
  PoImportDropzone,
  ReviewHint,
  ReviewTag,
} from '@/components/procurement/PoImportReview';
import { reviewFieldClass, toneFor, type ReviewIssue } from '@/components/procurement/poImportTone';
import { Checkbox } from '@/components/ui/Checkbox';
import { DownstreamConfirmDialog } from '@/components/corrections/DownstreamConfirmDialog';
import { useAuth } from '@/app/AuthProvider';
import { canWrite } from '@/lib/permissions';
import { useItems, useLocations, type Item } from '@/features/inventory/hooks';
import {
  ORDER_METHODS,
  ORDER_METHOD_LABELS,
  ORDER_PLATFORM_SUGGESTIONS,
  PO_MANUAL_STATUSES,
  PO_STATUS_LABELS,
  LEGACY_CARD_METHODS,
  PO_CHARGE_KINDS,
  PO_CHARGE_KIND_LABELS,
  PURCHASE_METHODS,
  PURCHASE_METHOD_LABELS,
  TRACKING_STATUS_LABELS,
  isSelectablePurchaseCard,
  lastGoodsCostCents,
  openPoAttachment,
  poPriceBecomesCurrent,
  purchaseCardLabel,
  purchaseOrderTrackingUrl,
  removePoFiles,
  useDeletePoAttachment,
  useItemCostSummaries,
  usePurchaseCards,
  useRefreshPurchaseOrderTracking,
  useSavePurchaseOrder,
  useUploadPoAttachment,
  useSupplierPartNumbers,
  useVendorItemTerms,
  useVendors,
  type CreateVendorInput,
  type OrderMethod,
  type PoAttachment,
  type PoAttachmentKind,
  type PoChargeKind,
  type PoShipment,
  type PoStatus,
  type PurchaseMethod,
  type PurchaseOrder,
  type SavePurchaseOrderInput,
  type VendorItemTerms,
} from '@/features/procurement/hooks';
import {
  formatQtyUom,
  formatUnitCostCents,
  isPackUnit,
  pluralUom,
  sameUom,
  supplierPricingUnit,
  toPriceUomCents,
  toPurchaseUnitCents,
  toStockQty,
  type PricingUnit,
} from '@/lib/uom';
import { UomSelect } from '@/components/inventory/UomSelect';
import {
  usePreviewPurchaseOrderChange,
  type DownstreamPreview,
} from '@/features/corrections/hooks';
import {
  buildPoImportDraft,
  chargeFieldKey,
  lineFieldKey,
  shipmentFieldKey,
  useExtractPoDocument,
  type PoImportField,
  type PoImportLineField,
  type Prefill,
} from '@/features/procurement/poImport';
import { cn } from '@/lib/utils';
import { formatError } from '@/lib/errors';
import {
  addDaysIsoDate,
  centsToInputDollars,
  centsToInputUnitDollars,
  formatCurrency,
  formatDateOnly,
  formatDateTime,
  formatUnitCurrency,
  inputDollarsToCents,
  inputDollarsToUnitCents,
  todayIsoDate,
} from '@/lib/formatters';

const NONE = '__none';
const NEW_VENDOR = '__new_vendor';
const CARD_PREFIX = 'card:';
const CUSTOM_DESC_MAX = 400;

interface LineDraft {
  key: string;
  id: string | null;
  itemId: string;
  sku: string;
  name: string;
  /** Unit the line is ordered and priced in. */
  uom: string;
  /** Stock units per `uom`. */
  conversion: number;
  /** Item's stock unit; empty for a custom line. */
  baseUom: string;
  quantity: string;
  unitCost: string;
  /** What the buyer is typing in the vendor's rate field ($/lb) while it has focus; unitCost follows it. */
  rateCost?: string;
  /** Tariff for the whole line, in dollars, or a percentage of the goods ("25%") until it is converted. */
  tariff: string;
  orderMethod: OrderMethod | '';
  externalRef: string;
  supplierPartNumber: string;
  /** Follows the vendor's part number from item_suppliers until the buyer edits it. */
  supplierPartNumberAuto: boolean;
  receivedQuantity: number;
}

interface ShippingChargeDraft {
  /** Existing row id, or a fresh uuid the RPC adopts so files can be attached before the row exists. */
  id: string;
  kind: PoChargeKind;
  description: string;
  reference: string;
  amount: string;
  chargedAt: string;
}

interface ShipmentDraft {
  key: string;
  carrier: string;
  trackingNumber: string;
  /** What is in this parcel, e.g. "Motors" — optional. */
  description: string;
  /** The saved row, for its carrier state; null for a shipment added in this session. */
  saved: PoShipment | null;
}

interface PendingFile {
  key: string;
  file: File;
  kind: PoAttachmentKind;
  shippingChargeId: string | null;
}

interface PurchaseOrderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Existing PO to edit; omit to create a new one. */
  purchaseOrder?: PurchaseOrder | null;
  /** When creating, pre-add this catalog item as the first line. */
  initialItemId?: string | null;
  onPrint?: (purchaseOrder: PurchaseOrder) => void;
}

let lineKeySeq = 0;
const nextLineKey = () => `line-${++lineKeySeq}`;

function draftLabel(line: LineDraft, index?: number): string {
  return line.sku || line.name.trim() || (index != null ? `line ${index + 1}` : 'custom item');
}

/** Ordered unit + conversion for an item from this vendor: its purchase unit when it has one. */
function vendorUnit(
  item: Item,
  terms: VendorItemTerms | undefined,
): { uom: string; conversion: number } {
  const conv = Number(terms?.units_per_purchase_uom ?? 1) || 1;
  if (terms?.purchase_uom && isPackUnit(conv)) return { uom: terms.purchase_uom, conversion: conv };
  return { uom: item.base_uom, conversion: 1 };
}

/** The vendor's per-lb (or other) rate, when this line is ordered in the unit that rate is set against. */
function linePricing(
  terms: VendorItemTerms | undefined,
  line: Pick<LineDraft, 'uom' | 'conversion'>,
): PricingUnit | null {
  const pricing = supplierPricingUnit(terms);
  if (!pricing || !terms || !sameUom(line.uom, terms.purchase_uom)) return null;
  return Math.abs(line.conversion - (Number(terms.units_per_purchase_uom) || 1)) < 1e-9 ? pricing : null;
}

function parseDollars(value: string): number {
  try {
    return inputDollarsToCents(value) ?? 0;
  } catch {
    return 0;
  }
}

function parseUnitDollars(value: string): number {
  try {
    return inputDollarsToUnitCents(value) ?? 0;
  } catch {
    return 0;
  }
}

/** Goods on a line before any tariff: quantity × unit cost, in whole cents. */
function lineGoodsCents(line: Pick<LineDraft, 'quantity' | 'unitCost'>): number {
  return Math.round((Number(line.quantity) || 0) * parseUnitDollars(line.unitCost));
}

/**
 * A tariff can be typed as dollars ("12.50") or as a share of the goods
 * ("25%", which electronics duties usually are). Throws on bad input.
 */
function tariffCentsFor(value: string, goodsCents: number): number {
  const trimmed = value.trim();
  if (!trimmed) return 0;
  if (trimmed.endsWith('%')) {
    const pct = Number.parseFloat(trimmed.slice(0, -1).replace(/,/g, ''));
    if (!Number.isFinite(pct) || pct < 0) {
      throw new Error(`“${value}” isn't a valid tariff percentage.`);
    }
    return Math.round((goodsCents * pct) / 100);
  }
  return inputDollarsToCents(trimmed) ?? 0;
}

function parseTariff(value: string, goodsCents: number): number {
  try {
    return tariffCentsFor(value, goodsCents);
  } catch {
    return 0;
  }
}

/** Per-purchase-unit vendor price converted to the unit a line is ordered in. */
function vendorPriceForLine(
  terms: VendorItemTerms,
  line: Pick<LineDraft, 'conversion'>,
): number | null {
  if (terms.unit_cost_cents == null) return null;
  const perPurchaseUnit = Number(terms.unit_cost_cents);
  const unitsPerPurchaseUom = Number(terms.units_per_purchase_uom ?? 1) || 1;
  return Math.round((perPurchaseUnit / unitsPerPurchaseUom) * line.conversion * 10_000) / 10_000;
}

/**
 * Saving a PO writes each catalog line's price back to the vendor's terms
 * for the item. Says so when the entered price differs from what is on
 * file; null when there is nothing to point out.
 */
function vendorPriceNote(
  terms: VendorItemTerms | undefined,
  line: LineDraft,
  orderedAt: string,
): string | null {
  const entered = parseUnitDollars(line.unitCost);
  if (!line.unitCost.trim() || entered <= 0) return null;
  if (!terms) return 'Saving adds this vendor to the item at this price.';
  const onFile = vendorPriceForLine(terms, line);
  if (onFile == null) return 'Saving sets this as the vendor’s price for this item.';
  if (Math.abs(onFile - entered) < 0.00005) return null;
  return poPriceBecomesCurrent(terms, orderedAt, line.id)
    ? `Vendor price on file is ${formatUnitCurrency(onFile)} — saving makes this the current price.`
    : `Vendor price on file is ${formatUnitCurrency(onFile)} from a newer order; this PO won’t change it.`;
}

function parseDays(value: string): number | null {
  if (value.trim() === '') return null;
  const n = Math.round(Number(value));
  return Number.isFinite(n) && n >= 0 ? n : null;
}

const IMPORT_FIELD_META: Record<PoImportField, { label: string; targetId: string }> = {
  vendor: { label: 'Vendor', targetId: 'po-vendor' },
  status: { label: 'Status', targetId: 'po-status' },
  purchaseMethod: { label: 'Purchase method', targetId: 'po-method' },
  paymentTerms: { label: 'Payment terms', targetId: 'po-terms' },
  shipTo: { label: 'Ship to', targetId: 'po-ship-to' },
  orderPlatform: { label: 'Placed on', targetId: 'po-platform' },
  orderReference: { label: 'Order #', targetId: 'po-order-ref' },
  orderedAt: { label: 'Order date', targetId: 'po-ordered' },
  expectedAt: { label: 'Expected delivery', targetId: 'po-expected' },
  tax: { label: 'Tax', targetId: 'po-tax' },
  processingFee: { label: 'Processing fees', targetId: 'po-processing-fee' },
  shippedAt: { label: 'Ship date', targetId: 'po-shipped' },
  notes: { label: 'Notes', targetId: 'po-notes' },
};
const LINES_META = { label: 'Line items', targetId: 'po-item-search' };
const LINE_FIELD_LABELS: Record<PoImportLineField, string> = {
  item: 'item',
  quantity: 'quantity',
  unitCost: 'unit cost',
  tariff: 'tariff',
  orderMethod: 'ordered via',
};

function lineTargetId(lineKey: string, field: PoImportLineField): string {
  return `po-line-${lineKey}-${field}`;
}

function shipmentTargetId(shipmentKey: string): string {
  return `po-shipment-${shipmentKey}-number`;
}

/** Label + control id for a prefill key; null when its line / charge / shipment is gone. */
function describeImportKey(
  key: string,
  lineIndex: Map<string, number>,
  charges: Map<string, PoChargeKind>,
  shipmentIndex: Map<string, number>,
): { label: string; targetId: string } | null {
  if (key.startsWith('line:')) {
    const [, lineKey, field] = key.split(':') as [string, string, PoImportLineField];
    const i = lineIndex.get(lineKey);
    if (i == null || !(field in LINE_FIELD_LABELS)) return null;
    return {
      label: `Line ${i + 1} ${LINE_FIELD_LABELS[field]}`,
      targetId: lineTargetId(lineKey, field),
    };
  }
  if (key.startsWith('charge:')) {
    const id = key.slice('charge:'.length);
    const kind = charges.get(id);
    return kind
      ? { label: `${PO_CHARGE_KIND_LABELS[kind]} charge`, targetId: `po-charge-${id}-amount` }
      : null;
  }
  if (key.startsWith('shipment:')) {
    const shipmentKey = key.slice('shipment:'.length);
    const i = shipmentIndex.get(shipmentKey);
    return i == null
      ? null
      : { label: `Tracking # ${i + 1}`, targetId: shipmentTargetId(shipmentKey) };
  }
  return IMPORT_FIELD_META[key as PoImportField] ?? null;
}

function prefillTitle(p: Prefill | undefined): string | undefined {
  if (!p) return undefined;
  return p.evidence ? `From the PDF: “${p.evidence}”` : 'From the PDF';
}

interface AttachmentListProps {
  existing: PoAttachment[];
  pending: PendingFile[];
  onAdd: (files: File[]) => void;
  onRemoveExisting: (attachment: PoAttachment) => void;
  onRemovePending: (key: string) => void;
  addLabel: string;
  ariaLabel: string;
}

function AttachmentList({
  existing,
  pending,
  onAdd,
  onRemoveExisting,
  onRemovePending,
  addLabel,
  ariaLabel,
}: AttachmentListProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const chip =
    'inline-flex max-w-[16rem] items-center gap-1 rounded border border-border bg-card px-1.5 py-0.5 text-xs';
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {existing.map((a) => (
        <span key={a.id} className={chip}>
          <FileText className="h-3 w-3 shrink-0 text-muted-foreground" />
          <button
            type="button"
            className="truncate hover:underline"
            title={`Open ${a.file_name}`}
            onClick={() =>
              openPoAttachment(a).catch((err) =>
                toast.error(err instanceof Error ? err.message : 'Could not open the file.'),
              )
            }
          >
            {a.file_name}
          </button>
          <button
            type="button"
            className="text-muted-foreground hover:text-foreground"
            onClick={() => onRemoveExisting(a)}
            aria-label={`Remove ${a.file_name}`}
          >
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}
      {pending.map((p) => (
        <span key={p.key} className={`${chip} border-dashed`} title="Uploads when you save">
          <FileText className="h-3 w-3 shrink-0 text-muted-foreground" />
          <span className="truncate">{p.file.name}</span>
          <button
            type="button"
            className="text-muted-foreground hover:text-foreground"
            onClick={() => onRemovePending(p.key)}
            aria-label={`Remove ${p.file.name}`}
          >
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-7 px-2 text-xs"
        onClick={() => inputRef.current?.click()}
      >
        <Paperclip className="mr-1 h-3.5 w-3.5" />
        {addLabel}
      </Button>
      <input
        ref={inputRef}
        type="file"
        multiple
        className="hidden"
        aria-label={ariaLabel}
        accept="image/*,application/pdf,.pdf,.png,.jpg,.jpeg,.heic,.webp,.csv,.xlsx,.xls,.doc,.docx,.eml,.txt"
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          if (files.length) onAdd(files);
          e.target.value = '';
        }}
      />
    </div>
  );
}

/**
 * Create / edit a purchase order: vendor, status, purchase method, where the
 * order was placed (+ order # and receipt), production / shipping lead
 * times, ship-to, catalog lines and one-off custom items (with per-line
 * order method, vendor order # and tariff), shipping and other charges
 * (fuel surcharges, service fees…, each with its own invoice), one tracking
 * number per parcel, processing fees, and totals. Tariffs and the charges
 * (split evenly across the lines) land on each item's cost, and the form
 * shows that landed cost per line. A custom item is only a description on
 * this PO — it is not added to the catalog. Saved atomically through
 * `save_purchase_order`, which also makes each catalog line's price the
 * vendor's current price for that item; attachments upload once the PO
 * has an id.
 */
export function PurchaseOrderDialog({
  open,
  onOpenChange,
  purchaseOrder,
  initialItemId,
  onPrint,
}: PurchaseOrderDialogProps) {
  const isEdit = !!purchaseOrder;
  const { role } = useAuth();
  // Mirrors the `vendors_write_admin` RLS policy.
  const canCreateVendor = canWrite(role, 'procurement');
  const save = useSavePurchaseOrder();
  const refreshTracking = useRefreshPurchaseOrderTracking();
  const previewChange = usePreviewPurchaseOrderChange();
  const { data: vendors } = useVendors();
  const [newVendorOpen, setNewVendorOpen] = useState(false);
  const { data: locations } = useLocations();
  const { data: items } = useItems();
  const { data: costSummaries } = useItemCostSummaries();
  const { data: purchaseCards, isLoading: cardsLoading } = usePurchaseCards();
  const uploadAttachment = useUploadPoAttachment();
  const deleteAttachment = useDeletePoAttachment();

  const [poNumber, setPoNumber] = useState('');
  const [vendorId, setVendorId] = useState('');
  const [status, setStatus] = useState<PoStatus>('internal_po');
  const [purchaseMethod, setPurchaseMethod] = useState<PurchaseMethod | ''>('');
  const [purchaseCardId, setPurchaseCardId] = useState('');
  const [paymentTerms, setPaymentTerms] = useState('');
  const [orderPlatform, setOrderPlatform] = useState('');
  const [orderReference, setOrderReference] = useState('');
  const [orderedAt, setOrderedAt] = useState('');
  const [productionLeadDays, setProductionLeadDays] = useState('');
  const [shippingLeadDays, setShippingLeadDays] = useState('');
  const [expectedAt, setExpectedAt] = useState('');
  const [expectedTouched, setExpectedTouched] = useState(false);
  const [shipToId, setShipToId] = useState('');
  const [tax, setTax] = useState('');
  const [processingFee, setProcessingFee] = useState('');
  const [charges, setCharges] = useState<ShippingChargeDraft[]>([]);
  const [attachments, setAttachments] = useState<PoAttachment[]>([]);
  const [removedAttachments, setRemovedAttachments] = useState<PoAttachment[]>([]);
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  const [finishing, setFinishing] = useState(false);
  const [shipments, setShipments] = useState<ShipmentDraft[]>([]);
  const [shippedAt, setShippedAt] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<LineDraft[]>([]);
  const [itemQuery, setItemQuery] = useState('');
  const [replacingKey, setReplacingKey] = useState<string | null>(null);
  const [focusLineKey, setFocusLineKey] = useState<string | null>(null);
  const [focusShipmentKey, setFocusShipmentKey] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingPreview, setPendingPreview] = useState<DownstreamPreview | null>(null);
  const [pendingInput, setPendingInput] = useState<SavePurchaseOrderInput | null>(null);
  const extractDocument = useExtractPoDocument();
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importSummary, setImportSummary] = useState<string | null>(null);
  const [importTotalCents, setImportTotalCents] = useState<number | null>(null);
  const [importVendor, setImportVendor] = useState<CreateVendorInput | null>(null);
  // Keyed by PoImportField, lineFieldKey() or chargeFieldKey(); an entry is
  // dropped as soon as the person edits that field.
  const [prefill, setPrefill] = useState<Record<string, Prefill>>({});
  const [importHints, setImportHints] = useState<Record<string, string>>({});
  const [reviewed, setReviewed] = useState(false);
  const importFileKey = useRef<string | null>(null);

  const { data: vendorTerms } = useVendorItemTerms(vendorId || null);
  const { data: supplierPartNumbers } = useSupplierPartNumbers();

  useEffect(() => {
    if (!open) return;
    const po = purchaseOrder;
    setPoNumber(po?.po_number ?? '');
    setVendorId(po?.vendor_id ?? '');
    setStatus(po?.status ?? 'internal_po');
    setPurchaseMethod(po?.purchase_method ?? '');
    setPurchaseCardId(po?.purchase_card_id ?? '');
    setPaymentTerms(po?.payment_terms ?? '');
    setOrderPlatform(po?.order_platform ?? '');
    setOrderReference(po?.order_reference ?? '');
    setOrderedAt(po?.ordered_at ?? (po ? '' : todayIsoDate()));
    const hasSplitLead =
      po?.production_lead_time_days != null || po?.shipping_lead_time_days != null;
    setProductionLeadDays(
      hasSplitLead
        ? po?.production_lead_time_days != null
          ? String(po.production_lead_time_days)
          : ''
        : po?.lead_time_days != null
          ? String(po.lead_time_days)
          : '',
    );
    setShippingLeadDays(
      po?.shipping_lead_time_days != null ? String(po.shipping_lead_time_days) : '',
    );
    setExpectedAt(po?.expected_at ?? '');
    setExpectedTouched(!!po?.expected_at);
    setShipToId(po?.ship_to_location_id ?? '');
    setTax(po && po.tax_cents ? centsToInputDollars(po.tax_cents) : '');
    setProcessingFee(
      po && po.processing_fee_cents ? centsToInputDollars(po.processing_fee_cents) : '',
    );
    setCharges(
      (po?.po_shipping_charges ?? []).map((c) => ({
        id: c.id,
        kind: c.kind ?? 'shipping',
        description: c.description ?? '',
        reference: c.reference ?? '',
        amount: centsToInputDollars(c.amount_cents),
        chargedAt: c.charged_at ?? '',
      })),
    );
    setAttachments(po?.po_attachments ?? []);
    setRemovedAttachments([]);
    setPendingFiles([]);
    setShipments(
      (po?.po_shipments ?? []).map((s) => ({
        key: nextLineKey(),
        carrier: s.carrier ?? '',
        trackingNumber: s.tracking_number,
        description: s.description ?? '',
        saved: s,
      })),
    );
    setShippedAt(po?.shipped_at ?? '');
    setNotes(po?.notes ?? '');
    setItemQuery('');
    setReplacingKey(null);
    setFocusLineKey(null);
    setFocusShipmentKey(null);
    setConfirmOpen(false);
    setPendingPreview(null);
    setPendingInput(null);
    setImportFile(null);
    setImportSummary(null);
    setImportTotalCents(null);
    setImportVendor(null);
    setPrefill({});
    setImportHints({});
    setReviewed(false);
    importFileKey.current = null;
    setLines(
      (po?.po_lines ?? []).map((l) => ({
        key: nextLineKey(),
        id: l.id,
        itemId: l.item_id ?? '',
        sku: l.items?.sku ?? '',
        name: l.items?.name ?? l.description ?? '',
        uom: l.uom,
        conversion: Number(l.conversion_to_base) || 1,
        baseUom: l.items?.base_uom ?? '',
        quantity: String(l.quantity),
        unitCost: centsToInputUnitDollars(l.unit_price_cents),
        tariff: l.tariff_cents ? centsToInputDollars(l.tariff_cents) : '',
        orderMethod: l.order_method ?? '',
        externalRef: l.external_order_ref ?? '',
        supplierPartNumber: l.supplier_part_number ?? '',
        supplierPartNumberAuto: !!l.item_id && !l.supplier_part_number,
        receivedQuantity: l.received_quantity,
      })),
    );
  }, [open, purchaseOrder]);

  // Default the ship-to to the first warehouse once locations load.
  useEffect(() => {
    if (!open || isEdit || shipToId || !locations?.length) return;
    const warehouse = locations.find((l) => l.kind === 'warehouse') ?? locations[0];
    setShipToId(warehouse.id);
  }, [open, isEdit, shipToId, locations]);

  const productionLead = parseDays(productionLeadDays);
  const shippingLead = parseDays(shippingLeadDays);
  const totalLead =
    productionLead == null && shippingLead == null
      ? null
      : (productionLead ?? 0) + (shippingLead ?? 0);

  // Expected delivery follows order date + total lead time until edited by hand.
  useEffect(() => {
    if (expectedTouched || totalLead == null) return;
    setExpectedAt(addDaysIsoDate(orderedAt || todayIsoDate(), totalLead));
  }, [orderedAt, totalLead, expectedTouched]);

  const changeVendor = (nextVendorId: string) => {
    if (nextVendorId === vendorId) return;
    setLines((prev) =>
      prev.map((l) =>
        l.itemId &&
        !l.supplierPartNumberAuto &&
        (!l.supplierPartNumber.trim() ||
          vendorTerms?.get(l.itemId)?.supplier_part_number?.trim() === l.supplierPartNumber.trim())
          ? { ...l, supplierPartNumberAuto: true }
          : l,
      ),
    );
    setVendorId(nextVendorId);
  };

  // Lines still on their default supplier part number follow the vendor's mapping.
  useEffect(() => {
    if (!vendorTerms) return;
    setLines((prev) => {
      let changed = false;
      const next = prev.map((l) => {
        if (!l.supplierPartNumberAuto || !l.itemId) return l;
        const pn = vendorTerms.get(l.itemId)?.supplier_part_number?.trim() ?? '';
        if (pn === l.supplierPartNumber) return l;
        changed = true;
        return { ...l, supplierPartNumber: pn };
      });
      return changed ? next : prev;
    });
  }, [vendorTerms, open, purchaseOrder]);

  /**
   * Catalog items whose internal SKU, any vendor's part number, or name
   * contains the query. Exact and prefix hits on a part number or SKU rank
   * first so pasting a number from a quote lands on the right item.
   */
  const itemMatches = useMemo(() => {
    const q = itemQuery.trim().toLowerCase();
    if (!q) return [];
    const rank = (value: string) => {
      const v = value.toLowerCase();
      if (v === q) return 0;
      if (v.startsWith(q)) return 1;
      if (v.includes(q)) return 2;
      return null;
    };
    const matches: Array<{ item: Item; score: number; matchedPartNumber: string | null }> = [];
    for (const it of items ?? []) {
      if (!it.is_active || it.status === 'archived') continue;
      let score = rank(it.sku);
      let matchedPartNumber: string | null = null;
      for (const s of supplierPartNumbers?.get(it.id) ?? []) {
        const r = rank(s.supplier_part_number);
        if (r == null) continue;
        const preferred = s.vendor_id === vendorId;
        if (score == null || r < score || (r === score && preferred)) {
          score = r;
          matchedPartNumber = s.supplier_part_number;
        }
      }
      const nameRank = rank(it.name);
      if (nameRank != null && (score == null || nameRank + 1 < score)) {
        score = nameRank + 1;
        matchedPartNumber = null;
      }
      if (score != null) matches.push({ item: it, score, matchedPartNumber });
    }
    return matches
      .sort((a, b) => a.score - b.score || a.item.sku.localeCompare(b.item.sku))
      .slice(0, 8);
  }, [items, itemQuery, supplierPartNumbers, vendorId]);

  const addItem = (item: Item) => {
    if (item.status === 'archived') {
      toast.error('Archived items can’t be added to a purchase order.');
      return;
    }
    const terms = vendorTerms?.get(item.id);
    const supplierPartNumber = terms?.supplier_part_number?.trim() ?? '';
    if (replacingKey) {
      setLines((prev) =>
        prev.map((l) =>
          l.key === replacingKey
            ? {
                ...l,
                itemId: item.id,
                sku: item.sku,
                name: item.name,
                baseUom: item.base_uom,
                ...vendorUnit(item, terms),
                supplierPartNumber,
                supplierPartNumberAuto: true,
              }
            : l,
        ),
      );
      touchLine(replacingKey, 'item');
      setReplacingKey(null);
      setItemQuery('');
      return;
    }
    const unit = vendorUnit(item, terms);
    // The vendor's price for the item alone — freight and duty land on the cost, not the PO line.
    const stockCost = lastGoodsCostCents(costSummaries?.get(item.id)) ?? null;
    const cost =
      terms?.unit_cost_cents ??
      (stockCost != null
        ? Math.round(Number(stockCost) * unit.conversion * 10_000) / 10_000
        : null);
    const reorderQty = item.reorder_qty
      ? Math.max(1, Math.ceil(item.reorder_qty / unit.conversion))
      : 1;
    const lead = terms?.lead_time_days ?? item.lead_time_worst_case_days ?? null;
    setLines((prev) => [
      ...prev,
      {
        key: nextLineKey(),
        id: null,
        itemId: item.id,
        sku: item.sku,
        name: item.name,
        ...unit,
        baseUom: item.base_uom,
        quantity: String(reorderQty),
        unitCost: centsToInputUnitDollars(cost),
        tariff: '',
        orderMethod: prev[prev.length - 1]?.orderMethod ?? '',
        externalRef: '',
        supplierPartNumber,
        supplierPartNumberAuto: true,
        receivedQuantity: 0,
      },
    ]);
    if (lead != null && (productionLead == null || productionLead < lead)) {
      setProductionLeadDays(String(lead));
    }
    setItemQuery('');
  };

  const seededItemRef = useRef(false);
  useEffect(() => {
    if (!open) {
      seededItemRef.current = false;
      return;
    }
    if (isEdit || !initialItemId || seededItemRef.current || !items) return;
    seededItemRef.current = true;
    const item = items.find((it) => it.id === initialItemId);
    if (item) addItem(item);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- seed once per open, not on every addItem identity change
  }, [open, isEdit, initialItemId, items]);

  const addCustomItem = (description: string) => {
    const desc = description.trim().slice(0, CUSTOM_DESC_MAX);
    if (replacingKey) {
      setLines((prev) =>
        prev.map((l) =>
          l.key === replacingKey
            ? {
                ...l,
                itemId: '',
                sku: '',
                name: desc,
                uom: l.uom || 'each',
                conversion: 1,
                baseUom: '',
                supplierPartNumber: l.supplierPartNumberAuto ? '' : l.supplierPartNumber,
                supplierPartNumberAuto: false,
              }
            : l,
        ),
      );
      touchLine(replacingKey, 'item');
      setReplacingKey(null);
      setItemQuery('');
      setFocusLineKey(replacingKey);
      return;
    }
    const key = nextLineKey();
    setLines((prev) => [
      ...prev,
      {
        key,
        id: null,
        itemId: '',
        sku: '',
        name: desc,
        uom: 'each',
        conversion: 1,
        baseUom: '',
        quantity: '1',
        unitCost: '',
        tariff: '',
        orderMethod: prev[prev.length - 1]?.orderMethod ?? '',
        externalRef: '',
        supplierPartNumber: '',
        supplierPartNumberAuto: false,
        receivedQuantity: 0,
      },
    ]);
    setFocusLineKey(key);
    setItemQuery('');
  };

  const updateLine = (key: string, patch: Partial<LineDraft>) =>
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  /**
   * Switch a line between the stock unit and the vendor's purchase unit,
   * keeping the amount and the money the same: 4 rolls at $45 ↔ 7,200 in at $0.025.
   */
  const switchLineUnit = (line: LineDraft, toPurchaseUnit: boolean) => {
    const item = (items ?? []).find((it) => it.id === line.itemId);
    if (!item) return;
    const target = toPurchaseUnit
      ? vendorUnit(item, vendorTerms?.get(item.id))
      : { uom: item.base_uom, conversion: 1 };
    if (target.conversion === line.conversion && target.uom === line.uom) return;
    const stockQty = (Number(line.quantity) || 0) * line.conversion;
    const qty = stockQty > 0 ? Math.max(1, Math.ceil(stockQty / target.conversion - 1e-9)) : 0;
    const perStockUnit = parseUnitDollars(line.unitCost) / line.conversion;
    updateLine(line.key, {
      ...target,
      quantity: qty ? String(qty) : line.quantity,
      unitCost: line.unitCost.trim()
        ? centsToInputUnitDollars(Math.round(perStockUnit * target.conversion * 10_000) / 10_000)
        : '',
    });
  };

  const removeLine = (key: string) => setLines((prev) => prev.filter((l) => l.key !== key));

  const addCharge = (kind: PoChargeKind) =>
    setCharges((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        kind,
        description: '',
        reference: '',
        amount: '',
        chargedAt: todayIsoDate(),
      },
    ]);

  /** Converts a "25%" tariff into dollars once the buyer leaves the field. */
  const settleTariff = (line: LineDraft) => {
    if (!line.tariff.trim().endsWith('%')) return;
    try {
      const cents = tariffCentsFor(line.tariff, lineGoodsCents(line));
      updateLine(line.key, { tariff: cents ? centsToInputDollars(cents) : '' });
    } catch {
      // Left as typed; buildInput reports it on save.
    }
  };

  const updateCharge = (id: string, patch: Partial<ShippingChargeDraft>) =>
    setCharges((prev) => prev.map((c) => (c.id === id ? { ...c, ...patch } : c)));

  const removeCharge = (id: string) => {
    setCharges((prev) => prev.filter((c) => c.id !== id));
    setPendingFiles((prev) => prev.filter((p) => p.shippingChargeId !== id));
  };

  const addShipment = () => {
    const key = nextLineKey();
    setShipments((prev) => [
      ...prev,
      { key, carrier: '', trackingNumber: '', description: '', saved: null },
    ]);
    setFocusShipmentKey(key);
  };

  const updateShipment = (key: string, patch: Partial<ShipmentDraft>) =>
    setShipments((prev) => prev.map((s) => (s.key === key ? { ...s, ...patch } : s)));

  const removeShipment = (key: string) => {
    setShipments((prev) => prev.filter((s) => s.key !== key));
    touch(shipmentFieldKey(key));
  };

  const addFiles = (files: File[], kind: PoAttachmentKind, shippingChargeId: string | null) =>
    setPendingFiles((prev) => [
      ...prev,
      ...files.map((file) => ({ key: nextLineKey(), file, kind, shippingChargeId })),
    ]);

  const removePending = (key: string) =>
    setPendingFiles((prev) => prev.filter((p) => p.key !== key));

  const removeExisting = (attachment: PoAttachment) => {
    setAttachments((prev) => prev.filter((a) => a.id !== attachment.id));
    setRemovedAttachments((prev) => [...prev, attachment]);
  };

  const touch = (...keys: string[]) => {
    if (!importFile) return;
    const drop = <T,>(prev: Record<string, T>) => {
      if (!keys.some((k) => k in prev)) return prev;
      const next = { ...prev };
      for (const k of keys) delete next[k];
      return next;
    };
    setPrefill(drop);
    setImportHints(drop);
  };
  const touchLine = (key: string, ...fields: PoImportLineField[]) =>
    touch(...fields.map((f) => lineFieldKey(key, f)));

  const importOrder = async (file: File) => {
    let extraction;
    try {
      extraction = await extractDocument.mutateAsync(file);
    } catch (err) {
      toast.error(formatError(err, 'Could not read that document.'));
      return;
    }
    const draft = buildPoImportDraft(extraction, {
      vendors: vendors ?? [],
      items: items ?? [],
      locations: locations ?? [],
      cards: purchaseCards ?? [],
    });
    const nextPrefill: Record<string, Prefill> = { ...draft.prefill };
    const nextHints: Record<string, string> = { ...draft.hints };
    const nextLines: LineDraft[] = draft.lines.map((l) => {
      const key = nextLineKey();
      for (const [f, p] of Object.entries(l.prefill)) {
        nextPrefill[lineFieldKey(key, f as PoImportLineField)] = p;
      }
      for (const [f, h] of Object.entries(l.hints)) {
        nextHints[lineFieldKey(key, f as PoImportLineField)] = h;
      }
      return {
        key,
        id: null,
        itemId: l.itemId,
        sku: l.sku,
        name: l.name,
        uom: l.uom,
        conversion: 1,
        baseUom: l.itemId ? l.uom : '',
        quantity: l.quantity,
        unitCost: l.unitCost,
        tariff: l.tariff,
        orderMethod: l.orderMethod,
        externalRef: '',
        supplierPartNumber: l.supplierPartNumber,
        supplierPartNumberAuto: !!l.itemId && !l.supplierPartNumber,
        receivedQuantity: 0,
      };
    });
    const nextCharges: ShippingChargeDraft[] = draft.charges.map((c) => {
      const id = crypto.randomUUID();
      nextPrefill[chargeFieldKey(id)] = c.prefill;
      return {
        id,
        kind: c.kind,
        description: c.description,
        reference: c.reference,
        amount: c.amount,
        chargedAt: c.chargedAt || todayIsoDate(),
      };
    });
    const nextShipments: ShipmentDraft[] = draft.shipments.map((s) => {
      const key = nextLineKey();
      nextPrefill[shipmentFieldKey(key)] = s.prefill;
      return {
        key,
        carrier: s.carrier,
        trackingNumber: s.trackingNumber,
        description: s.description,
        saved: null,
      };
    });

    setVendorId(draft.vendorId ?? '');
    setStatus(draft.status);
    setPurchaseMethod(draft.purchaseMethod);
    setPurchaseCardId(draft.purchaseCardId);
    setPaymentTerms(draft.paymentTerms);
    if (draft.shipToId) setShipToId(draft.shipToId);
    setOrderPlatform(draft.orderPlatform);
    setOrderReference(draft.orderReference);
    setOrderedAt(draft.orderedAt);
    setProductionLeadDays('');
    setShippingLeadDays('');
    setExpectedAt(draft.expectedAt);
    setExpectedTouched(!!draft.expectedAt);
    setTax(draft.tax);
    setProcessingFee(draft.processingFee);
    setShipments(nextShipments);
    setShippedAt(draft.shippedAt);
    setNotes(draft.notes);
    setLines(nextLines);
    setCharges(nextCharges);
    const receiptKey = nextLineKey();
    setPendingFiles((prev) => [
      ...prev.filter((p) => p.key !== importFileKey.current && !p.shippingChargeId),
      { key: receiptKey, file, kind: 'order_receipt', shippingChargeId: null },
    ]);
    importFileKey.current = receiptKey;
    setImportFile(file);
    setImportSummary(draft.summary);
    setImportTotalCents(draft.documentTotalCents);
    setImportVendor(draft.newVendor);
    setPrefill(nextPrefill);
    setImportHints(nextHints);
    setReviewed(false);
    setItemQuery('');
    setReplacingKey(null);
    toast.success('Order read — review the highlighted fields before creating the PO.');
  };

  const openImportFile = () => {
    if (!importFile) return;
    const url = URL.createObjectURL(importFile);
    window.open(url, '_blank', 'noopener');
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  const focusIssue = (issue: ReviewIssue) => {
    const el = document.getElementById(issue.targetId);
    if (!el) return;
    el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    el.focus({ preventScroll: true });
  };

  const orderReceipts = attachments.filter((a) => !a.shipping_charge_id);
  const pendingReceipts = pendingFiles.filter((p) => !p.shippingChargeId);

  const subtotalCents = lines.reduce((acc, l) => acc + lineGoodsCents(l), 0);
  const tariffCents = lines.reduce((acc, l) => acc + parseTariff(l.tariff, lineGoodsCents(l)), 0);
  const shippingCharges = charges.filter((c) => c.kind === 'shipping');
  const otherCharges = charges.filter((c) => c.kind !== 'shipping');
  const shippingChargeCount = shippingCharges.length;
  const otherChargeCount = otherCharges.length;
  const shippingCents = shippingCharges.reduce((acc, c) => acc + parseDollars(c.amount), 0);
  const otherChargesCents = otherCharges.reduce((acc, c) => acc + parseDollars(c.amount), 0);
  const totalCents =
    subtotalCents +
    tariffCents +
    parseDollars(tax) +
    shippingCents +
    otherChargesCents +
    parseDollars(processingFee);
  // What lands on the items' cost: goods, tariffs, and the charges split
  // evenly across the lines (tax and processing fees stay out).
  const landedCents = subtotalCents + tariffCents + shippingCents + otherChargesCents;
  const chargeShareCents = lines.length ? (shippingCents + otherChargesCents) / lines.length : 0;

  const reviewIssues = useMemo(() => {
    const map = new Map<string, ReviewIssue>();
    if (!importFile) return map;
    const add = (key: string, issue: ReviewIssue) => {
      if (!map.has(key)) map.set(key, issue);
    };
    const required = (key: PoImportField | 'lines', hint?: string) => {
      const meta = key === 'lines' ? LINES_META : IMPORT_FIELD_META[key];
      add(key, { ...meta, tone: 'required', hint: importHints[key] ?? hint });
    };
    if (!vendorId) required('vendor', 'Pick a vendor.');
    if (!purchaseMethod) required('purchaseMethod', 'How was this paid?');
    else if (purchaseMethod === 'card' && !purchaseCardId) {
      required('purchaseMethod', 'Pick which card was used.');
    }
    if (!orderedAt) required('orderedAt', 'When was the order placed?');
    if (!shipToId) required('shipTo', 'Where is this shipping?');
    if (lines.length === 0) {
      required('lines', 'No items were found on the document — add what was ordered.');
    }
    lines.forEach((l, i) => {
      const qty = Number(l.quantity);
      const lineIssue = (field: PoImportLineField, hint: string) => {
        const key = lineFieldKey(l.key, field);
        add(key, {
          targetId: lineTargetId(l.key, field),
          label: `Line ${i + 1} ${LINE_FIELD_LABELS[field]}`,
          tone: 'required',
          hint: importHints[key] ?? hint,
        });
      };
      if (!Number.isInteger(qty) || qty <= 0)
        lineIssue('quantity', 'Enter a whole-number quantity.');
      if (!l.unitCost.trim()) lineIssue('unitCost', 'Enter the unit cost.');
      if (!l.itemId && !l.name.trim())
        lineIssue('item', 'Describe the item or pick it from the catalog.');
    });

    const lineIndex = new Map(lines.map((l, i) => [l.key, i]));
    const chargeKinds = new Map(charges.map((c) => [c.id, c.kind]));
    const shipmentIndex = new Map(shipments.map((s, i) => [s.key, i]));
    const check = (key: string, hint: string) => {
      const meta = describeImportKey(key, lineIndex, chargeKinds, shipmentIndex);
      if (meta) add(key, { ...meta, tone: 'check', hint });
    };
    for (const [key, hint] of Object.entries(importHints)) check(key, hint);
    for (const [key, p] of Object.entries(prefill)) {
      if (p.confidence === 'low') check(key, 'Low-confidence read — compare it with the PDF.');
    }
    if (importTotalCents != null && Math.abs(importTotalCents - totalCents) > 1) {
      add('total', {
        targetId: 'po-tax',
        label: 'Total differs from the PDF',
        tone: 'check',
        hint: `The PDF total is ${formatCurrency(importTotalCents)}; this PO adds up to ${formatCurrency(
          totalCents,
        )} (${formatCurrency(Math.abs(importTotalCents - totalCents))} ${
          importTotalCents > totalCents ? 'less' : 'more'
        }). Check prices, tariffs, tax, shipping, fees and discounts.`,
      });
    }
    return map;
  }, [
    importFile,
    importHints,
    prefill,
    vendorId,
    purchaseMethod,
    purchaseCardId,
    orderedAt,
    shipToId,
    lines,
    charges,
    shipments,
    importTotalCents,
    totalCents,
  ]);
  const issueList = useMemo(() => Array.from(reviewIssues.values()), [reviewIssues]);
  const requiredIssues = issueList.filter((i) => i.tone === 'required');
  const toneOf = (key: string) => toneFor(prefill[key], reviewIssues.get(key)?.tone);
  const fieldClass = (key: string) => reviewFieldClass(toneOf(key));
  const tagFor = (key: string) => (
    <ReviewTag tone={toneOf(key)} prefill={prefill[key]} hint={reviewIssues.get(key)?.hint} />
  );
  const hintFor = (key: string) => (
    <ReviewHint tone={toneOf(key)}>{reviewIssues.get(key)?.hint}</ReviewHint>
  );

  const cardOptions = useMemo(
    () =>
      (purchaseCards ?? []).filter((c) => isSelectablePurchaseCard(c) || c.id === purchaseCardId),
    [purchaseCards, purchaseCardId],
  );
  const otherMethodOptions = useMemo(
    () =>
      PURCHASE_METHODS.filter(
        (m) => m !== 'card' && (!LEGACY_CARD_METHODS.includes(m) || m === purchaseMethod),
      ),
    [purchaseMethod],
  );
  const methodValue =
    purchaseMethod === 'card' && purchaseCardId
      ? `${CARD_PREFIX}${purchaseCardId}`
      : purchaseMethod || NONE;
  const onMethodChange = (v: string) => {
    if (v.startsWith(CARD_PREFIX)) {
      setPurchaseMethod('card');
      setPurchaseCardId(v.slice(CARD_PREFIX.length));
    } else {
      setPurchaseMethod(v === NONE ? '' : (v as PurchaseMethod));
      setPurchaseCardId('');
    }
  };

  const statusOptions = useMemo<PoStatus[]>(
    () =>
      PO_MANUAL_STATUSES.includes(status) ? PO_MANUAL_STATUSES : [status, ...PO_MANUAL_STATUSES],
    [status],
  );

  const buildInput = (): SavePurchaseOrderInput | null => {
    if (!vendorId) {
      toast.error('Pick a vendor.');
      return null;
    }
    if (lines.length === 0) {
      toast.error('Add at least one item.');
      return null;
    }
    if (purchaseMethod === 'card' && !purchaseCardId) {
      toast.error('Pick which card was used.');
      return null;
    }
    let taxCents: number | null;
    let processingFeeCents: number | null;
    const lineInputs = [];
    const chargeInputs = [];
    const shipmentInputs = [];
    try {
      taxCents = inputDollarsToCents(tax);
      processingFeeCents = inputDollarsToCents(processingFee);
      const seenNumbers = new Set<string>();
      for (const s of shipments) {
        const number = s.trackingNumber.trim();
        if (!number) continue;
        if (seenNumbers.has(number.toLowerCase())) {
          throw new Error(`Tracking number ${number} is listed twice.`);
        }
        seenNumbers.add(number.toLowerCase());
        shipmentInputs.push({
          carrier: s.carrier.trim() || null,
          tracking_number: number,
          description: s.description.trim() || null,
        });
      }
      for (const [i, c] of charges.entries()) {
        const amount = inputDollarsToCents(c.amount);
        if (amount == null) {
          throw new Error(`${PO_CHARGE_KIND_LABELS[c.kind]} charge ${i + 1} needs an amount.`);
        }
        chargeInputs.push({
          id: c.id,
          kind: c.kind,
          description: c.description || null,
          reference: c.reference || null,
          amount_cents: amount,
          charged_at: c.chargedAt || null,
        });
      }
      for (const [i, l] of lines.entries()) {
        const qty = Number(l.quantity);
        const label = draftLabel(l, i);
        if (!l.itemId && !l.name.trim()) {
          throw new Error(`Line ${i + 1} needs a description.`);
        }
        if (!Number.isInteger(qty) || qty <= 0) {
          throw new Error(`Line ${i + 1} (${label}) needs a whole-number quantity above zero.`);
        }
        const unitPriceCents = inputDollarsToUnitCents(l.unitCost) ?? 0;
        lineInputs.push({
          id: l.id,
          item_id: l.itemId || null,
          description: l.itemId ? null : l.name.trim(),
          uom: l.uom.trim() || 'each',
          conversion_to_base: l.itemId ? l.conversion : 1,
          quantity: qty,
          unit_price_cents: unitPriceCents,
          tariff_cents: tariffCentsFor(l.tariff, Math.round(qty * unitPriceCents)),
          order_method: l.orderMethod || null,
          external_order_ref: l.externalRef || null,
          supplier_part_number: l.supplierPartNumber.trim() || null,
        });
      }
    } catch (err) {
      toast.error(formatError(err, 'Check the form for errors.'));
      return null;
    }
    return {
      id: purchaseOrder?.id ?? null,
      po_number: poNumber || null,
      vendor_id: vendorId,
      status,
      purchase_method: purchaseMethod || null,
      purchase_card_id: purchaseMethod === 'card' ? purchaseCardId || null : null,
      payment_terms: purchaseMethod === 'terms' ? paymentTerms || null : null,
      order_platform: orderPlatform || null,
      order_reference: orderReference || null,
      ordered_at: orderedAt || null,
      expected_at: expectedAt || null,
      production_lead_time_days: productionLead,
      shipping_lead_time_days: shippingLead,
      lead_time_days: totalLead,
      ship_to_location_id: shipToId || null,
      tax_cents: taxCents,
      processing_fee_cents: processingFeeCents,
      shipping_charges: chargeInputs,
      shipments: shipmentInputs,
      shipped_at: shippedAt || null,
      notes: notes || null,
      lines: lineInputs,
    };
  };

  const finishFiles = async (poId: string) => {
    setFinishing(true);
    const failures: string[] = [];
    const keptChargeIds = new Set(charges.map((c) => c.id));
    // Attachments of deleted charges were cascaded by the RPC; only their bytes remain.
    const orphaned = (purchaseOrder?.po_attachments ?? []).filter(
      (a) => a.shipping_charge_id && !keptChargeIds.has(a.shipping_charge_id),
    );
    const orphanedIds = new Set(orphaned.map((a) => a.id));
    await removePoFiles(orphaned.map((a) => a.storage_path)).catch(() => undefined);
    for (const a of removedAttachments) {
      if (orphanedIds.has(a.id)) continue;
      try {
        await deleteAttachment.mutateAsync(a);
      } catch {
        failures.push(`remove ${a.file_name}`);
      }
    }
    for (const p of pendingFiles) {
      try {
        await uploadAttachment.mutateAsync({
          purchaseOrderId: poId,
          file: p.file,
          kind: p.kind,
          shippingChargeId: p.shippingChargeId,
        });
      } catch {
        failures.push(`upload ${p.file.name}`);
      }
    }
    setFinishing(false);
    return failures;
  };

  const persist = async (input: SavePurchaseOrderInput) => {
    const savedId = await save.mutateAsync(input);
    const failures = await finishFiles(savedId);
    if (input.shipments?.length) {
      void refreshTracking.mutateAsync(savedId).catch((error) => {
        toast.warning(
          error instanceof Error
            ? `PO saved, but carrier tracking could not refresh: ${error.message}`
            : 'PO saved, but carrier tracking could not refresh.',
        );
      });
    }
    if (failures.length) {
      toast.warning(`Purchase order saved, but could not ${failures.join(', ')}.`);
    } else {
      toast.success(isEdit ? `Saved ${purchaseOrder?.po_number}` : 'Purchase order created');
    }
    setConfirmOpen(false);
    onOpenChange(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (importFile && !isEdit) {
      if (requiredIssues.length > 0) {
        toast.error(
          `Fill in the highlighted fields first: ${requiredIssues.map((i) => i.label).join(', ')}.`,
        );
        focusIssue(requiredIssues[0]);
        return;
      }
      if (!reviewed) {
        toast.error('Confirm you’ve checked the pre-filled values against the PDF.');
        return;
      }
    }
    const input = buildInput();
    if (!input) return;
    if (!isEdit) {
      try {
        await persist(input);
      } catch (err) {
        toast.error(formatError(err, 'Could not save the purchase order.'));
      }
      return;
    }
    try {
      const result = await previewChange.mutateAsync(input);
      if (result.severity === 'none') {
        await persist(input);
        return;
      }
      setPendingInput(input);
      setPendingPreview(result);
      setConfirmOpen(true);
    } catch (err) {
      toast.error(formatError(err, 'Could not save the purchase order.'));
    }
  };

  const confirmSave = async () => {
    if (!pendingInput) return;
    try {
      await persist(pendingInput);
    } catch (err) {
      toast.error(formatError(err, 'Could not save the purchase order.'));
    }
  };

  const submitting =
    save.isPending ||
    previewChange.isPending ||
    refreshTracking.isPending ||
    finishing ||
    uploadAttachment.isPending ||
    deleteAttachment.isPending;
  const showOrderDetails =
    !['internal_po', 'cancelled'].includes(status) ||
    !!orderPlatform ||
    !!orderReference ||
    orderReceipts.length > 0 ||
    pendingReceipts.length > 0;
  const reopening =
    isEdit &&
    lines.some((l) => l.receivedQuantity > 0) &&
    PO_MANUAL_STATUSES.includes(status) &&
    status !== purchaseOrder?.status;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-5xl overflow-y-auto">
        <form onSubmit={handleSubmit} className="min-w-0">
          <DialogHeader>
            <DialogTitle>
              {isEdit ? `Purchase order ${purchaseOrder?.po_number}` : 'New purchase order'}
            </DialogTitle>
          </DialogHeader>

          <div className="mt-5 space-y-6">
            {!isEdit &&
              (importFile && !extractDocument.isPending ? (
                <PoImportBanner
                  fileName={importFile.name}
                  summary={importSummary}
                  prefilledCount={Object.keys(prefill).length}
                  issues={issueList}
                  onOpenFile={openImportFile}
                  onReplace={(file) => void importOrder(file)}
                  onReject={(message) => toast.error(message)}
                  onFocusIssue={focusIssue}
                />
              ) : (
                <PoImportDropzone
                  busy={extractDocument.isPending}
                  onFile={(file) => void importOrder(file)}
                  onReject={(message) => toast.error(message)}
                />
              ))}

            <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div className="space-y-1.5 lg:col-span-2">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <Label htmlFor="po-vendor">
                      Vendor <span className="text-fresh-salmon-dark">*</span>
                    </Label>
                    {tagFor('vendor')}
                  </div>
                  {canCreateVendor && (
                    <button
                      type="button"
                      onClick={() => setNewVendorOpen(true)}
                      className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
                    >
                      <Plus className="h-3 w-3" />
                      New vendor
                    </button>
                  )}
                </div>
                <Select
                  value={vendorId}
                  onValueChange={(v) => {
                    if (v === NEW_VENDOR) {
                      setNewVendorOpen(true);
                      return;
                    }
                    changeVendor(v);
                    touch('vendor');
                  }}
                >
                  <SelectTrigger id="po-vendor" className={fieldClass('vendor')}>
                    <SelectValue placeholder="Pick a vendor" />
                  </SelectTrigger>
                  <SelectContent>
                    {(vendors ?? [])
                      .filter((v) => v.is_active || v.id === vendorId)
                      .map((v) => (
                        <SelectItem key={v.id} value={v.id}>
                          {v.name}
                          {!v.is_active && (
                            <span className="ml-1.5 text-muted-foreground">(inactive)</span>
                          )}
                        </SelectItem>
                      ))}
                    {canCreateVendor && (
                      <>
                        {(vendors ?? []).length > 0 && <SelectSeparator />}
                        <SelectItem value={NEW_VENDOR} className="font-medium">
                          + Create new vendor…
                        </SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>
                {hintFor('vendor')}
                {importVendor && !vendorId && canCreateVendor && (
                  <button
                    type="button"
                    onClick={() => setNewVendorOpen(true)}
                    className="inline-flex items-center gap-1 text-xs font-medium text-info hover:underline"
                  >
                    <Plus className="h-3 w-3" />
                    Create “{importVendor.name}” with the details from the PDF
                  </button>
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="po-number">PO number</Label>
                <Input
                  id="po-number"
                  value={poNumber}
                  onChange={(e) => setPoNumber(e.target.value)}
                  placeholder="Auto (PO-0001)"
                  maxLength={40}
                  className="font-mono text-xs"
                />
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <Label htmlFor="po-status">Status</Label>
                  {tagFor('status')}
                </div>
                <Select
                  value={status}
                  onValueChange={(v) => {
                    setStatus(v as PoStatus);
                    touch('status');
                  }}
                >
                  <SelectTrigger id="po-status" className={fieldClass('status')}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {statusOptions.map((s) => (
                      <SelectItem key={s} value={s} disabled={!PO_MANUAL_STATUSES.includes(s)}>
                        {PO_STATUS_LABELS[s]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {reopening && (
                  <p className="text-xs text-fresh-salmon-dark">
                    Saving this status reverses receiving and adjusts on-hand. You’ll be asked to
                    confirm.
                  </p>
                )}
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <Label htmlFor="po-method">Purchase method</Label>
                  {tagFor('purchaseMethod')}
                </div>
                <Select
                  value={methodValue}
                  onValueChange={(v) => {
                    onMethodChange(v);
                    touch('purchaseMethod');
                  }}
                >
                  <SelectTrigger id="po-method" className={fieldClass('purchaseMethod')}>
                    <SelectValue placeholder="Pick a card" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>Not set</SelectItem>
                    <SelectSeparator />
                    <SelectGroup>
                      <SelectLabel>Cards</SelectLabel>
                      {cardOptions.length === 0 ? (
                        <SelectItem value="__no_cards" disabled>
                          {cardsLoading ? 'Loading cards…' : 'No cards on Card mapping yet'}
                        </SelectItem>
                      ) : (
                        cardOptions.map((c) => {
                          const detail = [c.network, c.assignee_names.join(', ') || c.account_name]
                            .filter(Boolean)
                            .join(' · ');
                          return (
                            <SelectItem key={c.id} value={`${CARD_PREFIX}${c.id}`}>
                              <span className="flex items-center gap-2">
                                <span className="tabular-nums">{purchaseCardLabel(c)}</span>
                                {detail && (
                                  <span className="text-xs text-muted-foreground">{detail}</span>
                                )}
                              </span>
                            </SelectItem>
                          );
                        })
                      )}
                    </SelectGroup>
                    <SelectSeparator />
                    <SelectGroup>
                      <SelectLabel>Other</SelectLabel>
                      {otherMethodOptions.map((m) => (
                        <SelectItem key={m} value={m}>
                          {PURCHASE_METHOD_LABELS[m]}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
                {hintFor('purchaseMethod')}
              </div>
              {purchaseMethod === 'terms' && (
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2">
                    <Label htmlFor="po-terms">Payment terms</Label>
                    {tagFor('paymentTerms')}
                  </div>
                  <Input
                    id="po-terms"
                    value={paymentTerms}
                    onChange={(e) => {
                      setPaymentTerms(e.target.value);
                      touch('paymentTerms');
                    }}
                    className={fieldClass('paymentTerms')}
                    placeholder="e.g. Net 30"
                    maxLength={60}
                  />
                </div>
              )}
              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <Label htmlFor="po-ship-to">Ship to</Label>
                  {tagFor('shipTo')}
                </div>
                <Select
                  value={shipToId || NONE}
                  onValueChange={(v) => {
                    setShipToId(v === NONE ? '' : v);
                    touch('shipTo');
                  }}
                >
                  <SelectTrigger id="po-ship-to" className={fieldClass('shipTo')}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>Not set</SelectItem>
                    {(locations ?? []).map((loc) => (
                      <SelectItem key={loc.id} value={loc.id}>
                        {loc.name} ({loc.code})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {hintFor('shipTo')}
              </div>
            </section>

            {showOrderDetails ? (
              <section className="space-y-3 rounded-md border border-border p-4">
                <Label className="text-sm">Order placed</Label>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-2">
                      <Label htmlFor="po-platform" className="font-normal text-muted-foreground">
                        Placed on
                      </Label>
                      {tagFor('orderPlatform')}
                    </div>
                    <Input
                      id="po-platform"
                      value={orderPlatform}
                      onChange={(e) => {
                        setOrderPlatform(e.target.value);
                        touch('orderPlatform');
                      }}
                      className={fieldClass('orderPlatform')}
                      placeholder="e.g. Alibaba"
                      list="po-platform-options"
                      maxLength={80}
                    />
                    <datalist id="po-platform-options">
                      {ORDER_PLATFORM_SUGGESTIONS.map((p) => (
                        <option key={p} value={p} />
                      ))}
                    </datalist>
                  </div>
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-2">
                      <Label htmlFor="po-order-ref" className="font-normal text-muted-foreground">
                        Order #
                      </Label>
                      {tagFor('orderReference')}
                    </div>
                    <Input
                      id="po-order-ref"
                      value={orderReference}
                      onChange={(e) => {
                        setOrderReference(e.target.value);
                        touch('orderReference');
                      }}
                      placeholder="Platform / vendor order number"
                      maxLength={120}
                      className={cn('font-mono text-xs', fieldClass('orderReference'))}
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <span className="text-xs text-muted-foreground">Order receipt</span>
                  <AttachmentList
                    existing={orderReceipts}
                    pending={pendingReceipts}
                    onAdd={(files) => addFiles(files, 'order_receipt', null)}
                    onRemoveExisting={removeExisting}
                    onRemovePending={removePending}
                    addLabel="Attach receipt"
                    ariaLabel="Attach order receipt"
                  />
                </div>
              </section>
            ) : null}

            <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <Label htmlFor="po-ordered">Order date</Label>
                  {tagFor('orderedAt')}
                </div>
                <Input
                  id="po-ordered"
                  type="date"
                  value={orderedAt}
                  onChange={(e) => {
                    setOrderedAt(e.target.value);
                    touch('orderedAt');
                  }}
                  className={fieldClass('orderedAt')}
                />
                {hintFor('orderedAt')}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="po-lead-production">Production lead time (days)</Label>
                <Input
                  id="po-lead-production"
                  value={productionLeadDays}
                  onChange={(e) => setProductionLeadDays(e.target.value.replace(/[^\d]/g, ''))}
                  inputMode="numeric"
                  placeholder="e.g. 21"
                  className="tabular-nums"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="po-lead-shipping">Shipping lead time (days)</Label>
                <Input
                  id="po-lead-shipping"
                  value={shippingLeadDays}
                  onChange={(e) => setShippingLeadDays(e.target.value.replace(/[^\d]/g, ''))}
                  inputMode="numeric"
                  placeholder="e.g. 30"
                  className="tabular-nums"
                />
                {totalLead != null && (
                  <p className="text-xs text-muted-foreground">
                    Total lead time: {totalLead} days.
                  </p>
                )}
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <Label htmlFor="po-expected">Vendor expected delivery</Label>
                  {tagFor('expectedAt')}
                </div>
                <Input
                  id="po-expected"
                  type="date"
                  value={expectedAt}
                  onChange={(e) => {
                    setExpectedAt(e.target.value);
                    setExpectedTouched(e.target.value !== '');
                    touch('expectedAt');
                  }}
                  className={fieldClass('expectedAt')}
                />
              </div>
            </section>

            <section className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Label>Line items</Label>
                  {tagFor('lines')}
                </div>
                {lines.length > 0 && (
                  <span className="text-xs text-muted-foreground">
                    {lines.length} item{lines.length === 1 ? '' : 's'}
                  </span>
                )}
              </div>

              <div className="flex items-start gap-2">
                <div className="relative min-w-0 flex-1">
                  <div
                    className={cn(
                      'flex items-center gap-2 rounded-md border border-border px-3 py-2',
                      fieldClass('lines'),
                    )}
                  >
                    <Search className="h-3.5 w-3.5 text-muted-foreground" />
                    <input
                      id="po-item-search"
                      value={itemQuery}
                      onChange={(e) => setItemQuery(e.target.value)}
                      placeholder={
                        replacingKey
                          ? 'Search by SKU, supplier part #, or name, or type a custom description…'
                          : 'Search by SKU, supplier part #, or name, or type a one-off description…'
                      }
                      aria-label="Search line items"
                      className="w-full bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
                    />
                  </div>
                  {replacingKey && (
                    <button
                      type="button"
                      className="mt-1 text-xs font-medium text-muted-foreground hover:text-foreground"
                      onClick={() => setReplacingKey(null)}
                    >
                      Cancel item change
                    </button>
                  )}
                  {itemQuery.trim() && (
                    <div className="mt-1 max-h-60 overflow-y-auto rounded-md border border-border bg-card shadow-sm">
                      {itemMatches.length === 0 ? (
                        <div className="px-3 py-3 text-center text-xs text-muted-foreground">
                          No catalog items match.
                        </div>
                      ) : (
                        itemMatches.map(({ item: it, matchedPartNumber }) => {
                          const terms = vendorTerms?.get(it.id);
                          const unit = vendorUnit(it, terms);
                          const stockCost = lastGoodsCostCents(costSummaries?.get(it.id));
                          const cost =
                            terms?.unit_cost_cents ??
                            (stockCost != null
                              ? Math.round(Number(stockCost) * unit.conversion)
                              : null);
                          const partNumber =
                            matchedPartNumber ?? (terms?.supplier_part_number?.trim() || null);
                          return (
                            <button
                              key={it.id}
                              type="button"
                              onClick={() => addItem(it)}
                              className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-secondary/60"
                            >
                              <Plus className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                              <span className="shrink-0 font-mono text-xs text-muted-foreground">
                                {it.sku}
                              </span>
                              <span className="truncate">{it.name}</span>
                              {partNumber && (
                                <span
                                  className="shrink-0 rounded bg-secondary px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground"
                                  title="Supplier part #"
                                >
                                  {partNumber}
                                </span>
                              )}
                              {cost != null && (
                                <span className="ml-auto shrink-0 text-xs tabular-nums text-muted-foreground">
                                  {formatCurrency(cost)}/{unit.uom}
                                </span>
                              )}
                            </button>
                          );
                        })
                      )}
                      <button
                        type="button"
                        onClick={() => addCustomItem(itemQuery)}
                        className="flex w-full items-start gap-2 border-t border-border px-3 py-2 text-left text-sm hover:bg-secondary/60"
                      >
                        <Plus className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                        <span className="min-w-0 truncate font-medium">
                          Add “{itemQuery.trim()}” as a custom item
                        </span>
                      </button>
                    </div>
                  )}
                </div>
                <Button
                  type="button"
                  variant="outline"
                  className="shrink-0"
                  onClick={() => addCustomItem(itemQuery)}
                >
                  <Plus className="mr-1 h-3.5 w-3.5" />
                  Custom item
                </Button>
              </div>

              {lines.length === 0 ? (
                <p className="rounded-md border border-dashed border-border bg-secondary/40 px-3 py-6 text-center text-xs text-muted-foreground">
                  No items yet.
                </p>
              ) : (
                <div className="overflow-x-auto rounded-md border border-border">
                  <table className="w-full text-sm">
                    <thead className="bg-secondary/50 text-xs text-muted-foreground">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium">Item</th>
                        <th className="w-36 px-2 py-2 text-left font-medium">Supplier part #</th>
                        <th className="w-24 px-2 py-2 text-right font-medium">Qty</th>
                        <th className="w-28 px-2 py-2 text-right font-medium">Unit cost</th>
                        <th className="w-24 px-2 py-2 text-right font-medium">
                          <span title="Import duty for the line. Type an amount, or a percentage of the goods like 25%.">
                            Tariff
                          </span>
                        </th>
                        <th className="w-36 px-2 py-2 text-left font-medium">Ordered via</th>
                        <th className="w-36 px-2 py-2 text-left font-medium">Vendor order #</th>
                        <th className="w-28 px-2 py-2 text-right font-medium">Total</th>
                        <th className="w-10 px-2 py-2" />
                      </tr>
                    </thead>
                    <tbody>
                      {lines.map((l) => {
                        const goodsCents = lineGoodsCents(l);
                        const lineTariff = parseTariff(l.tariff, goodsCents);
                        const lineTotal = goodsCents + lineTariff;
                        const label = draftLabel(l);
                        const custom = !l.itemId;
                        const lineItem = custom
                          ? null
                          : (items ?? []).find((it) => it.id === l.itemId);
                        const lineTerms = custom ? undefined : vendorTerms?.get(l.itemId);
                        const packUnit = lineItem ? vendorUnit(lineItem, lineTerms) : null;
                        const packed = isPackUnit(l.conversion);
                        const pricing = custom ? null : linePricing(lineTerms, l);
                        const canSwitchUnit =
                          !!lineItem &&
                          (packed || (packUnit != null && isPackUnit(packUnit.conversion)));
                        const stockQty = toStockQty(Number(l.quantity) || 0, l.conversion);
                        const itemKey = lineFieldKey(l.key, 'item');
                        const qtyKey = lineFieldKey(l.key, 'quantity');
                        const costKey = lineFieldKey(l.key, 'unitCost');
                        const tariffKey = lineFieldKey(l.key, 'tariff');
                        const methodKey = lineFieldKey(l.key, 'orderMethod');
                        const itemTone = toneOf(itemKey);
                        const qty = Number(l.quantity) || 0;
                        const landedUnitCents =
                          qty > 0 ? (goodsCents + lineTariff + chargeShareCents) / qty : null;
                        const showLanded =
                          landedUnitCents != null && (lineTariff > 0 || chargeShareCents > 0);
                        const priceNote =
                          !custom && vendorId && vendorTerms
                            ? vendorPriceNote(lineTerms, l, orderedAt)
                            : null;
                        return (
                          <tr key={l.key} className="border-t border-border align-top">
                            <td
                              className={cn(
                                'px-3 py-2',
                                itemTone && !custom && reviewFieldClass(itemTone),
                                itemTone && !custom && 'border-l-2',
                              )}
                            >
                              {custom ? (
                                <div className="space-y-1">
                                  <div className="flex items-center gap-2">
                                    <Badge variant="muted">Custom</Badge>
                                    {tagFor(itemKey)}
                                    <button
                                      type="button"
                                      className="shrink-0 text-xs font-medium text-muted-foreground hover:text-foreground"
                                      onClick={() => {
                                        setReplacingKey(l.key);
                                        setItemQuery('');
                                      }}
                                    >
                                      Use catalog item
                                    </button>
                                  </div>
                                  <Input
                                    id={lineTargetId(l.key, 'item')}
                                    value={l.name}
                                    onChange={(e) => {
                                      updateLine(l.key, { name: e.target.value });
                                      touchLine(l.key, 'item');
                                    }}
                                    placeholder="Uline workstation, 72 in, with lower shelf"
                                    maxLength={CUSTOM_DESC_MAX}
                                    aria-label="Custom item description"
                                    autoFocus={l.key === focusLineKey}
                                    className={cn('h-8', fieldClass(itemKey))}
                                  />
                                  {hintFor(itemKey)}
                                </div>
                              ) : (
                                <>
                                  <div className="flex items-center gap-2">
                                    <span className="font-mono text-xs text-muted-foreground">
                                      {l.sku}
                                    </span>
                                    {tagFor(itemKey)}
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <span className="truncate">{l.name}</span>
                                    <button
                                      id={lineTargetId(l.key, 'item')}
                                      type="button"
                                      className="shrink-0 text-xs font-medium text-muted-foreground hover:text-foreground"
                                      onClick={() => {
                                        setReplacingKey(l.key);
                                        setItemQuery('');
                                      }}
                                    >
                                      Change
                                    </button>
                                  </div>
                                </>
                              )}
                              {l.receivedQuantity > 0 && (
                                <Badge variant="outline" className="mt-1">
                                  {formatQtyUom(l.receivedQuantity, l.uom)} received
                                </Badge>
                              )}
                            </td>
                            <td className="px-2 py-2">
                              <Input
                                value={l.supplierPartNumber}
                                onChange={(e) =>
                                  updateLine(l.key, {
                                    supplierPartNumber: e.target.value,
                                    supplierPartNumberAuto: false,
                                  })
                                }
                                placeholder="Optional"
                                maxLength={120}
                                aria-label={`Supplier part number for ${label}`}
                                className="h-8 font-mono text-xs"
                              />
                            </td>
                            <td className="px-2 py-2">
                              <Input
                                id={lineTargetId(l.key, 'quantity')}
                                value={l.quantity}
                                onChange={(e) => {
                                  updateLine(l.key, {
                                    quantity: e.target.value.replace(/[^\d]/g, ''),
                                  });
                                  touch(qtyKey);
                                }}
                                inputMode="numeric"
                                aria-label={`Quantity for ${label}`}
                                title={
                                  reviewIssues.get(qtyKey)?.hint ?? prefillTitle(prefill[qtyKey])
                                }
                                className={cn('h-8 text-right tabular-nums', fieldClass(qtyKey))}
                              />
                              {custom ? (
                                <UomSelect
                                  value={l.uom}
                                  onChange={(uom) => updateLine(l.key, { uom })}
                                  ariaLabel={`Unit of measure for ${label}`}
                                  className="mt-1 h-7 justify-end gap-1 text-[11px]"
                                />
                              ) : canSwitchUnit && lineItem && packUnit ? (
                                <Select
                                  value={packed ? 'purchase' : 'stock'}
                                  onValueChange={(v) => switchLineUnit(l, v === 'purchase')}
                                >
                                  <SelectTrigger
                                    className="mt-1 h-7 justify-end gap-1 text-[11px]"
                                    aria-label={`Unit for ${label}`}
                                  >
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="purchase">
                                      {packed ? pluralUom(l.uom, 2) : pluralUom(packUnit.uom, 2)}
                                    </SelectItem>
                                    <SelectItem value="stock">{lineItem.base_uom}</SelectItem>
                                  </SelectContent>
                                </Select>
                              ) : (
                                <div className="mt-0.5 text-right text-[11px] text-muted-foreground">
                                  {l.uom}
                                </div>
                              )}
                              {packed && l.baseUom && (
                                <div
                                  className="mt-0.5 text-right text-[11px] tabular-nums text-muted-foreground"
                                  title={`1 ${l.uom} = ${formatQtyUom(l.conversion, l.baseUom)}`}
                                >
                                  = {formatQtyUom(stockQty, l.baseUom)} to stock
                                </div>
                              )}
                              {pricing && qty > 0 && (
                                <div
                                  className="mt-0.5 text-right text-[11px] tabular-nums text-muted-foreground"
                                  title={`About ${formatQtyUom(pricing.perPurchaseUom, pricing.priceUom)} per ${l.uom}; the vendor bills what it ships.`}
                                >
                                  ≈ {formatQtyUom(qty * pricing.perPurchaseUom, pricing.priceUom)}
                                </div>
                              )}
                            </td>
                            <td className="px-2 py-2">
                              <Input
                                id={lineTargetId(l.key, 'unitCost')}
                                value={l.unitCost}
                                onChange={(e) => {
                                  updateLine(l.key, { unitCost: e.target.value, rateCost: undefined });
                                  touch(costKey);
                                }}
                                inputMode="decimal"
                                placeholder="0.00"
                                aria-label={`Unit cost for ${label}`}
                                title={
                                  reviewIssues.get(costKey)?.hint ?? prefillTitle(prefill[costKey])
                                }
                                className={cn('h-8 text-right tabular-nums', fieldClass(costKey))}
                              />
                              <div className="mt-0.5 text-right text-[11px] tabular-nums text-muted-foreground">
                                per {l.uom || 'unit'}
                                {packed && l.baseUom && l.unitCost.trim() && (
                                  <span className="block">
                                    {formatUnitCostCents(
                                      parseUnitDollars(l.unitCost) / l.conversion,
                                    )}
                                    /{l.baseUom}
                                  </span>
                                )}
                              </div>
                              {pricing && (
                                <div className="mt-1 flex items-center justify-end gap-1 text-[11px] text-muted-foreground">
                                  <span>or $</span>
                                  <Input
                                    value={
                                      l.rateCost ??
                                      (l.unitCost.trim()
                                        ? centsToInputUnitDollars(
                                            toPriceUomCents(parseUnitDollars(l.unitCost), pricing),
                                          )
                                        : '')
                                    }
                                    onChange={(e) => {
                                      const typed = e.target.value;
                                      let cents: number | null;
                                      try {
                                        cents = inputDollarsToUnitCents(typed);
                                      } catch {
                                        updateLine(l.key, { rateCost: typed });
                                        return;
                                      }
                                      updateLine(l.key, {
                                        rateCost: typed,
                                        unitCost:
                                          cents == null
                                            ? ''
                                            : centsToInputUnitDollars(toPurchaseUnitCents(cents, pricing)),
                                      });
                                      touch(costKey);
                                    }}
                                    onBlur={() => updateLine(l.key, { rateCost: undefined })}
                                    inputMode="decimal"
                                    placeholder="0.00"
                                    aria-label={`Price per ${pricing.priceUom} for ${label}`}
                                    title={`Vendor's rate per ${pricing.priceUom}. 1 ${l.uom} ≈ ${formatQtyUom(pricing.perPurchaseUom, pricing.priceUom)}, so the price per ${l.uom} is this × ${formatQtyUom(pricing.perPurchaseUom, pricing.priceUom)}.`}
                                    className="h-6 w-20 px-1.5 text-right text-[11px] tabular-nums"
                                  />
                                  <span>/{pricing.priceUom}</span>
                                </div>
                              )}
                              {priceNote && (
                                <p
                                  className="mt-1 w-28 text-right text-[11px] leading-snug text-muted-foreground"
                                  title={priceNote}
                                >
                                  {priceNote}
                                </p>
                              )}
                            </td>
                            <td className="px-2 py-2">
                              {prefill[tariffKey] && (
                                <div className="mb-1 flex justify-end">{tagFor(tariffKey)}</div>
                              )}
                              <Input
                                id={lineTargetId(l.key, 'tariff')}
                                value={l.tariff}
                                onChange={(e) => {
                                  updateLine(l.key, { tariff: e.target.value });
                                  touch(tariffKey);
                                }}
                                onBlur={() => settleTariff(l)}
                                inputMode="decimal"
                                placeholder="0.00"
                                aria-label={`Tariff for ${label}`}
                                title={
                                  prefillTitle(prefill[tariffKey]) ??
                                  'Import duty for this line. Type an amount, or a percentage of the goods like 25%.'
                                }
                                className={cn('h-8 text-right tabular-nums', fieldClass(tariffKey))}
                              />
                              {lineTariff > 0 && goodsCents > 0 && (
                                <div className="mt-0.5 text-right text-[11px] tabular-nums text-muted-foreground">
                                  {((lineTariff / goodsCents) * 100).toFixed(1)}% of goods
                                </div>
                              )}
                            </td>
                            <td className="px-2 py-2">
                              <Select
                                value={l.orderMethod || NONE}
                                onValueChange={(v) => {
                                  updateLine(l.key, {
                                    orderMethod: v === NONE ? '' : (v as OrderMethod),
                                  });
                                  touch(methodKey);
                                }}
                              >
                                <SelectTrigger
                                  id={lineTargetId(l.key, 'orderMethod')}
                                  className={cn('h-8', fieldClass(methodKey))}
                                  aria-label={`Order method for ${label}`}
                                >
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value={NONE}>Not ordered yet</SelectItem>
                                  {ORDER_METHODS.map((m) => (
                                    <SelectItem key={m} value={m}>
                                      {ORDER_METHOD_LABELS[m]}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </td>
                            <td className="px-2 py-2">
                              <Input
                                value={l.externalRef}
                                onChange={(e) => updateLine(l.key, { externalRef: e.target.value })}
                                placeholder="Optional"
                                maxLength={120}
                                aria-label={`Vendor order number for ${label}`}
                                className="h-8 font-mono text-xs"
                              />
                            </td>
                            <td className="px-2 py-2 text-right tabular-nums">
                              {formatCurrency(lineTotal)}
                              {lineTariff > 0 && (
                                <div className="text-[11px] text-muted-foreground">
                                  incl. {formatCurrency(lineTariff)} tariff
                                </div>
                              )}
                              {showLanded && (
                                <div
                                  className="text-[11px] text-muted-foreground"
                                  title={`Landed cost per ${l.uom || 'unit'}: ${formatUnitCurrency(
                                    goodsCents / qty,
                                  )} goods + ${formatUnitCurrency(lineTariff / qty)} tariff + ${formatUnitCurrency(
                                    chargeShareCents / qty,
                                  )} share of shipping & other charges (split evenly across ${lines.length} line${
                                    lines.length === 1 ? '' : 's'
                                  }).`}
                                >
                                  Landed {formatUnitCurrency(landedUnitCents)}/{l.uom || 'unit'}
                                </div>
                              )}
                            </td>
                            <td className="px-2 py-2">
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => removeLine(l.key)}
                                title={
                                  l.receivedQuantity > 0
                                    ? 'Remove this line and reverse what was received'
                                    : 'Remove line'
                                }
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            <section className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <Label>Shipping &amp; other charges</Label>
                  <p className="text-xs text-muted-foreground">
                    Freight, fuel surcharges, service fees and the like, billed on top of the lines.
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => addCharge('shipping')}
                  >
                    <Plus className="mr-1 h-3.5 w-3.5" />
                    Add shipping charge
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => addCharge('service_fee')}
                  >
                    <Plus className="mr-1 h-3.5 w-3.5" />
                    Add fee / surcharge
                  </Button>
                </div>
              </div>
              {charges.length === 0 ? (
                <p className="rounded-md border border-dashed border-border bg-secondary/40 px-3 py-4 text-center text-xs text-muted-foreground">
                  No shipping or other charges yet.
                </p>
              ) : (
                <div className="overflow-x-auto rounded-md border border-border">
                  <table className="w-full text-sm">
                    <thead className="bg-secondary/50 text-xs text-muted-foreground">
                      <tr>
                        <th className="w-40 px-3 py-2 text-left font-medium">Type</th>
                        <th className="px-2 py-2 text-left font-medium">Description</th>
                        <th className="w-44 px-2 py-2 text-left font-medium">
                          Invoice / tracking #
                        </th>
                        <th className="w-36 px-2 py-2 text-left font-medium">Date</th>
                        <th className="w-28 px-2 py-2 text-right font-medium">Amount</th>
                        <th className="w-10 px-2 py-2" />
                      </tr>
                    </thead>
                    <tbody>
                      {charges.map((c, i) => {
                        const kindLabel = PO_CHARGE_KIND_LABELS[c.kind];
                        return (
                          <tr key={c.id} className="border-t border-border align-top">
                            <td className="px-3 py-2">
                              <Select
                                value={c.kind}
                                onValueChange={(v) =>
                                  updateCharge(c.id, { kind: v as PoChargeKind })
                                }
                              >
                                <SelectTrigger
                                  className="h-8"
                                  aria-label={`Type of charge ${i + 1}`}
                                >
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {PO_CHARGE_KINDS.map((k) => (
                                    <SelectItem key={k} value={k}>
                                      {PO_CHARGE_KIND_LABELS[k]}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </td>
                            <td className="px-2 py-2">
                              {prefill[chargeFieldKey(c.id)] && (
                                <div className="mb-1">{tagFor(chargeFieldKey(c.id))}</div>
                              )}
                              <Input
                                value={c.description}
                                onChange={(e) =>
                                  updateCharge(c.id, { description: e.target.value })
                                }
                                placeholder={
                                  c.kind === 'shipping'
                                    ? 'e.g. Flexport ocean freight'
                                    : c.kind === 'fuel_surcharge'
                                      ? 'e.g. Carrier fuel surcharge 12%'
                                      : 'e.g. Small order fee'
                                }
                                maxLength={200}
                                aria-label={`Description for ${kindLabel.toLowerCase()} charge ${i + 1}`}
                                className={cn('h-8', fieldClass(chargeFieldKey(c.id)))}
                              />
                              <div className="mt-1.5">
                                <AttachmentList
                                  existing={attachments.filter(
                                    (a) => a.shipping_charge_id === c.id,
                                  )}
                                  pending={pendingFiles.filter((p) => p.shippingChargeId === c.id)}
                                  onAdd={(files) => addFiles(files, 'shipping', c.id)}
                                  onRemoveExisting={removeExisting}
                                  onRemovePending={removePending}
                                  addLabel="Attach invoice"
                                  ariaLabel={`Attach invoice for ${kindLabel.toLowerCase()} charge ${i + 1}`}
                                />
                              </div>
                            </td>
                            <td className="px-2 py-2">
                              <Input
                                value={c.reference}
                                onChange={(e) => updateCharge(c.id, { reference: e.target.value })}
                                placeholder="Optional"
                                maxLength={200}
                                aria-label={`Reference for ${kindLabel.toLowerCase()} charge ${i + 1}`}
                                className="h-8 font-mono text-xs"
                              />
                            </td>
                            <td className="px-2 py-2">
                              <Input
                                type="date"
                                value={c.chargedAt}
                                onChange={(e) => updateCharge(c.id, { chargedAt: e.target.value })}
                                aria-label={`Date for ${kindLabel.toLowerCase()} charge ${i + 1}`}
                                className="h-8"
                              />
                            </td>
                            <td className="px-2 py-2">
                              <Input
                                id={`po-charge-${c.id}-amount`}
                                value={c.amount}
                                onChange={(e) => {
                                  updateCharge(c.id, { amount: e.target.value });
                                  touch(chargeFieldKey(c.id));
                                }}
                                inputMode="decimal"
                                placeholder="0.00"
                                aria-label={`Amount for ${kindLabel.toLowerCase()} charge ${i + 1}`}
                                title={prefillTitle(prefill[chargeFieldKey(c.id)])}
                                className={cn(
                                  'h-8 text-right tabular-nums',
                                  fieldClass(chargeFieldKey(c.id)),
                                )}
                              />
                            </td>
                            <td className="px-2 py-2">
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => removeCharge(c.id)}
                                title={`Remove ${kindLabel.toLowerCase()} charge`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            <section className="grid gap-6 lg:grid-cols-2">
              <div className="space-y-4">
                <div className="space-y-3 rounded-md border border-border p-3">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <Label>Shipment tracking</Label>
                      <p className="text-xs text-muted-foreground">
                        One tracking number per parcel; an order that ships in parts can have
                        several.
                      </p>
                    </div>
                    <Button type="button" variant="outline" size="sm" onClick={addShipment}>
                      <Plus className="mr-1 h-3.5 w-3.5" />
                      Add tracking number
                    </Button>
                  </div>
                  <div className="space-y-1.5 sm:w-48">
                    <div className="flex items-center gap-2">
                      <Label htmlFor="po-shipped">Ship date</Label>
                      {tagFor('shippedAt')}
                    </div>
                    <Input
                      id="po-shipped"
                      type="date"
                      value={shippedAt}
                      onChange={(e) => {
                        setShippedAt(e.target.value);
                        touch('shippedAt');
                      }}
                      className={fieldClass('shippedAt')}
                    />
                  </div>
                  {shipments.length === 0 ? (
                    <p className="rounded-md border border-dashed border-border bg-secondary/40 px-3 py-3 text-center text-xs text-muted-foreground">
                      No tracking numbers yet.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {shipments.map((s, i) => {
                        const key = shipmentFieldKey(s.key);
                        const saved = s.saved;
                        const unchanged =
                          !!saved && saved.tracking_number === s.trackingNumber.trim();
                        const href = unchanged
                          ? purchaseOrderTrackingUrl(saved)
                          : purchaseOrderTrackingUrl({
                              tracking_number: s.trackingNumber.trim() || null,
                              carrier: s.carrier,
                            });
                        return (
                          <div
                            key={s.key}
                            className="space-y-2 rounded-md border border-border p-2"
                          >
                            <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.6fr)_minmax(0,1.2fr)_auto]">
                              <Input
                                value={s.carrier}
                                onChange={(e) => updateShipment(s.key, { carrier: e.target.value })}
                                placeholder="Carrier (auto-detected)"
                                maxLength={60}
                                aria-label={`Carrier for tracking number ${i + 1}`}
                                className="h-8"
                              />
                              <div className="min-w-0">
                                {prefill[key] && <div className="mb-1">{tagFor(key)}</div>}
                                <Input
                                  id={shipmentTargetId(s.key)}
                                  value={s.trackingNumber}
                                  onChange={(e) => {
                                    updateShipment(s.key, { trackingNumber: e.target.value });
                                    touch(key);
                                  }}
                                  placeholder="Tracking number"
                                  maxLength={200}
                                  aria-label={`Tracking number ${i + 1}`}
                                  autoFocus={s.key === focusShipmentKey}
                                  title={prefillTitle(prefill[key])}
                                  className={cn('h-8 font-mono text-xs', fieldClass(key))}
                                />
                              </div>
                              <Input
                                value={s.description}
                                onChange={(e) =>
                                  updateShipment(s.key, { description: e.target.value })
                                }
                                placeholder="What's in it (optional)"
                                maxLength={200}
                                aria-label={`Contents of shipment ${i + 1}`}
                                className="h-8"
                              />
                              <div className="flex items-center gap-1">
                                {href && (
                                  <a
                                    href={href}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-secondary hover:text-foreground"
                                    title="Open carrier tracking page"
                                    aria-label={`Open carrier page for tracking number ${i + 1}`}
                                  >
                                    <ExternalLink className="h-4 w-4" />
                                  </a>
                                )}
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={() => removeShipment(s.key)}
                                  title="Remove tracking number"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                            {unchanged && saved.tracking_checked_at && (
                              <div className="grid gap-x-4 gap-y-1 rounded bg-secondary/50 p-2 text-xs text-muted-foreground sm:grid-cols-2">
                                <span>
                                  {saved.tracking_status
                                    ? TRACKING_STATUS_LABELS[saved.tracking_status]
                                    : 'Tracking added'}
                                  {' · ETA '}
                                  <strong className="font-medium text-foreground">
                                    {formatDateOnly(saved.tracking_estimated_delivery)}
                                  </strong>
                                </span>
                                <span>
                                  Last checked: {formatDateTime(saved.tracking_checked_at)}
                                </span>
                                {saved.tracking_last_event && (
                                  <span className="sm:col-span-2">
                                    {saved.tracking_last_event}
                                    {saved.tracking_last_location
                                      ? ` · ${saved.tracking_last_location}`
                                      : ''}
                                  </span>
                                )}
                                {saved.tracking_error && (
                                  <span className="text-fresh-salmon-dark sm:col-span-2">
                                    Tracking error: {saved.tracking_error}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2">
                    <Label htmlFor="po-notes">Notes</Label>
                    {tagFor('notes')}
                  </div>
                  <Textarea
                    id="po-notes"
                    value={notes}
                    onChange={(e) => {
                      setNotes(e.target.value);
                      touch('notes');
                    }}
                    className={fieldClass('notes')}
                    placeholder="Internal notes, quote reference, special instructions…"
                    rows={3}
                    maxLength={2000}
                  />
                </div>
              </div>

              <div className="space-y-2 rounded-md border border-border bg-secondary/30 p-4 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="tabular-nums">{formatCurrency(subtotalCents)}</span>
                </div>
                {tariffCents > 0 && (
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Tariffs</span>
                    <span className="tabular-nums">{formatCurrency(tariffCents)}</span>
                  </div>
                )}
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-2">
                    <Label htmlFor="po-tax" className="font-normal text-muted-foreground">
                      Tax
                    </Label>
                    {tagFor('tax')}
                  </div>
                  <Input
                    id="po-tax"
                    value={tax}
                    onChange={(e) => {
                      setTax(e.target.value);
                      touch('tax');
                    }}
                    inputMode="decimal"
                    placeholder="0.00"
                    className={cn('h-8 w-32 text-right tabular-nums', fieldClass('tax'))}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">
                    Shipping
                    {shippingChargeCount > 0 && (
                      <span className="ml-1 text-xs">
                        ({shippingChargeCount} charge{shippingChargeCount === 1 ? '' : 's'})
                      </span>
                    )}
                  </span>
                  <span className="tabular-nums">{formatCurrency(shippingCents)}</span>
                </div>
                {otherChargeCount > 0 && (
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">
                      Other charges
                      <span className="ml-1 text-xs">
                        ({otherChargeCount} charge{otherChargeCount === 1 ? '' : 's'})
                      </span>
                    </span>
                    <span className="tabular-nums">{formatCurrency(otherChargesCents)}</span>
                  </div>
                )}
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-2">
                    <Label
                      htmlFor="po-processing-fee"
                      className="font-normal text-muted-foreground"
                    >
                      Processing fees
                    </Label>
                    {tagFor('processingFee')}
                  </div>
                  <Input
                    id="po-processing-fee"
                    value={processingFee}
                    onChange={(e) => {
                      setProcessingFee(e.target.value);
                      touch('processingFee');
                    }}
                    inputMode="decimal"
                    placeholder="0.00"
                    className={cn('h-8 w-32 text-right tabular-nums', fieldClass('processingFee'))}
                  />
                </div>
                <div className="flex items-center justify-between border-t border-border pt-2 font-medium">
                  <span>Total</span>
                  <span className="tabular-nums">{formatCurrency(totalCents)}</span>
                </div>
                {lines.length > 0 &&
                  (tariffCents > 0 || shippingCents > 0 || otherChargesCents > 0) && (
                    <div
                      className="flex items-center justify-between text-xs text-muted-foreground"
                      title="Goods + tariffs + shipping + other charges. Tariffs stay with their line; shipping and other charges are split evenly across the lines. This is what lands on each item's cost once received. Tax and processing fees are not included."
                    >
                      <span>
                        Landed cost of goods
                        {shippingCents + otherChargesCents > 0 && (
                          <span className="ml-1">
                            ({formatCurrency(Math.round(chargeShareCents))} of charges per line)
                          </span>
                        )}
                      </span>
                      <span className="tabular-nums">{formatCurrency(landedCents)}</span>
                    </div>
                  )}
                {importTotalCents != null && (
                  <div
                    className={cn(
                      'flex items-center justify-between text-xs',
                      reviewIssues.has('total')
                        ? 'text-fresh-salmon-dark'
                        : 'text-muted-foreground',
                    )}
                  >
                    <span>Total on the PDF</span>
                    <span className="tabular-nums">{formatCurrency(importTotalCents)}</span>
                  </div>
                )}
                {hintFor('total')}
              </div>
            </section>
          </div>

          <DialogFooter className="mt-6 sm:items-center sm:justify-between">
            <div className="flex flex-wrap items-center gap-3">
              {purchaseOrder && onPrint && (
                <Button type="button" variant="outline" onClick={() => onPrint(purchaseOrder)}>
                  <Printer className="h-4 w-4" />
                  Print / PDF
                </Button>
              )}
              {importFile && !isEdit && (
                <label className="flex cursor-pointer items-center gap-2 text-sm">
                  <Checkbox
                    checked={reviewed}
                    onCheckedChange={setReviewed}
                    ariaLabel="I’ve checked the pre-filled values against the PDF"
                  />
                  <span>
                    I’ve checked the pre-filled values against the PDF
                    {requiredIssues.length > 0 && (
                      <span className="ml-1 text-xs text-fresh-salmon-dark">
                        ({requiredIssues.length} field{requiredIssues.length === 1 ? '' : 's'} still
                        need{requiredIssues.length === 1 ? 's' : ''} input)
                      </span>
                    )}
                  </span>
                </label>
              )}
            </div>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => onOpenChange(false)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={
                  submitting || extractDocument.isPending || (!!importFile && !isEdit && !reviewed)
                }
              >
                {submitting ? 'Saving…' : isEdit ? 'Save changes' : 'Create PO'}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
      {/* Rendered outside the PO <form> so its submit event doesn't bubble
          (through the React tree) into the PO's onSubmit. */}
      <NewSupplierDialog
        open={newVendorOpen}
        onOpenChange={setNewVendorOpen}
        onCreated={(id) => {
          changeVendor(id);
          touch('vendor');
        }}
        onUseExisting={(v) => {
          changeVendor(v.id);
          touch('vendor');
        }}
        initialValues={vendorId ? null : importVendor}
      />
      <DownstreamConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        preview={pendingPreview}
        confirming={save.isPending}
        onConfirm={() => void confirmSave()}
      />
    </Dialog>
  );
}
